import { IoClose } from 'react-icons/io5';
import { MdOutlineAccessTime } from 'react-icons/md';
import { Modal, ModalBody, ModalContent, ModalHeader, Spinner, Tooltip } from '@ds';
import { TmdxResourceCard } from '@features/components/ResourceCards';
import { CONTRACTOR_AFFILIATION_PREFERENCES } from '@lp/features/admin/forms/ContractorForm/types';
import { OrganSpecializationsList } from '@lp/features/RoutePlanner/components/OrganSpecializationsList/OrganSpecializationsList';
import SectionRow from '@features/components/CaseOverview/components/SectionRow/SectionRow';
import { CaseResource, CaseRetrievalSurgeonResource, ResourceLogisticsStatus } from '@/api/apis/case';
import { AffiliationType, EmployeeComposed } from '@/api/apis/resources';
import { displayPhoneNumber, getLastTrackedLocation, getLastTrackedTime, getLocationAddress } from '@/utils/helpers';
import { rsStatusName } from '@/constants/resources.constants';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { ViewResourceModalFooter } from './ViewResourceModalFooter';

const REMOVE_ALLOWED_STATUSES = [
  ResourceLogisticsStatus.Reserved,
  ResourceLogisticsStatus.BookingSent,
  ResourceLogisticsStatus.Booked,
];

export type ViewSurgeonModalProps = {
  title: string;
  resource?: CaseResource<CaseRetrievalSurgeonResource> | null;
  isOpen: boolean;
  isLoading: boolean;
  isAssignEnabled: boolean;
  isRemoveEnabled: boolean;
  size?: 'md' | 'xs' | 'sm' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full';
  onRemove: () => void;
  onAssign: () => void;
  onClose: () => void;
};

export const ViewSurgeonModal = ({
  title,
  resource,
  isOpen,
  isLoading,
  isAssignEnabled,
  isRemoveEnabled,
  size = 'md',
  onRemove,
  onAssign,
  onClose,
}: ViewSurgeonModalProps) => {
  const { resolveSubdivisionName } = useCountriesData();
  const last30Days = resource?.data
    ? `${resource.data.currentDeploymentData.successfulCasesLastThirtyDays}/${resource.data.currentDeploymentData.allCasesLastThirtyDays}`
    : 'N/A';

  const lastCall = resource?.data ? `${resource.data.currentDeploymentData.lastCallDaysAgo}d` : 'N/A';

  const isContractor = resource?.data?.isContractor;

  const renderAffiliation = () => {
    const affiliationTxCenterName = CONTRACTOR_AFFILIATION_PREFERENCES.find(
      (value) => value.value === AffiliationType.TXCenter,
    );

    const affiliationOpoName = CONTRACTOR_AFFILIATION_PREFERENCES.find((value) => value.value === AffiliationType.OPO);

    if (resource?.data?.affiliationTxCenter) {
      return (
        <>
          <div>{affiliationTxCenterName?.name}</div>
          <div>
            {resource?.data?.affiliationTxCenter.name}{' '}
            <span className="font-semibold">({resource?.data?.affiliationTxCenter?.code})</span>
          </div>
          <div>{getLocationAddress(resource?.data?.affiliationTxCenter.location, resolveSubdivisionName)}</div>
        </>
      );
    }

    if (resource?.data?.affiliationOpo) {
      return (
        <>
          <div>{affiliationOpoName?.name}</div>
          <div>
            {resource?.data?.affiliationOpo.name}{' '}
            <span className="font-semibold">({resource?.data?.affiliationOpo?.code})</span>
          </div>
          <div>{getLocationAddress(resource?.data?.affiliationOpo.location, resolveSubdivisionName)}</div>
        </>
      );
    }

    return 'N/A';
  };

  return (
    <Modal
      size={size}
      isOpen={isOpen}
      onClose={onClose}
      backdrop="opaque"
      closeButton={<IoClose size={45} style={{ top: 15 }} />}
      classNames={{
        base: 'px-6 bg-base-80',
        body: 'border-b-1 border-base-30 px-0 py-6 text-l-r',
        header: 'px-0 font-secondary text-hl! text-base-20',
        footer: 'px-0 flex gap-6',
        wrapper: 'z-420',
        backdrop: 'z-419',
      }}
    >
      <ModalContent>
        {() => (
          <>
            {isLoading && (
              <div className="absolute top-0 left-0 flex justify-center items-center z-50 w-full h-full bg-white bg-opacity-40">
                <Spinner size="md" />
              </div>
            )}
            <ModalHeader>{title}</ModalHeader>
            <ModalBody>
              <TmdxResourceCard
                className="bg-base-90 px-4 py-3"
                label="Actions"
                list={[]}
                type="user"
                title={`${(resource!.data! as EmployeeComposed).firstName} ${(resource!.data! as EmployeeComposed).lastName}`}
                subTitle={isContractor ? 'Contractor Retrieval Surgeon' : 'Retrieval Surgeon'}
                phoneNumber={displayPhoneNumber((resource!.data! as EmployeeComposed).phone)}
                isReadOnly
                menuDisabled
                chipProps={{
                  status: resource!.logisticsStatus,
                }}
                chipText={rsStatusName[resource!.logisticsStatus]}
                avatarProps={{
                  src: (resource!.data! as EmployeeComposed).profilePhoto,
                }}
              />
              <div className="flex flex-col gap-4 p-6 bg-base-80 rounded-xl shadow-s">
                <SectionRow label="Home location">
                  <Tooltip
                    content={
                      getLocationAddress((resource!.data! as EmployeeComposed).homeLocation, resolveSubdivisionName) ||
                      'N/A'
                    }
                  >
                    {getLocationAddress((resource!.data! as EmployeeComposed).homeLocation, resolveSubdivisionName) ||
                      'N/A'}
                  </Tooltip>
                </SectionRow>
                {isContractor && (
                  <SectionRow truncate={false} label="Affiliation">
                    {renderAffiliation()}
                  </SectionRow>
                )}
                <SectionRow label="Organ">
                  <OrganSpecializationsList
                    organs={(resource!.data! as EmployeeComposed).organSpecializations}
                    size="lg"
                    bgTone="bg60"
                  />
                </SectionRow>
                <SectionRow label="Current location">
                  <div className="flex items-center gap-2">
                    {!!resource?.data?.trackedLocation && (
                      <Tooltip content={getLastTrackedTime(resource.data.trackedLocation)}>
                        <div>
                          <MdOutlineAccessTime className="text-base-30" size={20} />
                        </div>
                      </Tooltip>
                    )}
                    <div className="truncate">{getLastTrackedLocation(resource?.data?.trackedLocation)}</div>
                  </div>
                </SectionRow>
                <SectionRow label="30 days">{last30Days}</SectionRow>
                <SectionRow label="Last call">{lastCall}</SectionRow>
              </div>
            </ModalBody>
            <ViewResourceModalFooter
              resource={resource}
              isLoading={isLoading}
              isAssignEnabled={isAssignEnabled}
              isRemoveEnabled={isRemoveEnabled}
              removeAllowedStatuses={REMOVE_ALLOWED_STATUSES}
              onAssign={onAssign}
              onRemove={onRemove}
            />
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
